import { useCallback, useEffect, useRef, useState } from 'react'
import { radioNowFeed, subscribeRadioNow } from 'state/radioNowFeed'
import {
    clearMediaTrackOrigin,
    getMediaPlaybackState,
    setMediaTrackOrigin,
    subscribeToMediaDock,
} from 'state/mediaPlaybackCoordinator'
import { radioSongMediaKey } from 'components/answers/radioCardMedia'
import { openMediaDockRadio, stopMediaDockRadio } from 'components/shared/MediaDock/mediaDockEvents'
import { finishRadioNow, radioFinishRetryDelayMs } from 'components/shared/MediaDock/mediaDockRadio'
import {
    clearInlineRadioPlay,
    rememberInlineRadioPlay,
} from 'components/shared/MediaDock/mediaDockRadioContext'

// Tuning in on a station song card (the Music header, a thread's ON AIR head)
// plays the song right there, from the station's position. The card is the
// song's on-screen origin: while it is visible the dock leaves the song
// playing in it, and takes it as the radio once the card leaves. A card that
// can show a video song's picture (`inlineVideo`, the Music header) plays it
// in place too; anywhere else a video song tunes in through the dock.
//
// `clock` is the play on air ({ playIndex, endsAt, position }), `position` the
// station's live position in it. The caller renders the hidden
// AudioPlayerCore with `coreRef` and `onPlaybackStateChange`. `onPlayEnded`
// runs after the station hears the song ended (the header carries on into the
// next song by itself). A card whose play goes off air (`live` false) stays
// inert, except that a session already playing keeps the song to its end.
// While the dock plays the station the card plays nothing and doesn't claim
// the song back; `playHere` lets the dock go and tunes in on the card.
export default function useInlineRadioTuneIn({
    cardRef,
    media,
    clock,
    position,
    receivedAt,
    dockTunedIn,
    source,
    live = true,
    onPlayEnded = null,
    inlineVideo = false,
    // The element the dock treats as the song's place on screen: the card, or
    // for a hosted video song the box its picture is parked over.
    originRef = cardRef,
}) {
    const [listening, setListening] = useState(false)
    const [inlinePlaying, setInlinePlaying] = useState(false)
    const coreRef = useRef(null)
    const mediaKey = (live || listening) && media?.url ? radioSongMediaKey(media) : null
    const playsHere =
        (media?.kind === 'audio' || (inlineVideo && media?.kind === 'video')) && Boolean(mediaKey)
    // Bumped whenever a session starts or ends, so a slow finish report never
    // hands over a session the listener already left.
    const sessionRef = useRef(0)
    useEffect(() => () => void sessionRef.current++, [])

    // A listener tuned in here counts like one tuned in on the dock.
    useEffect(
        () =>
            listening
                ? subscribeRadioNow(() => {}, { listening: true, contenders: false })
                : undefined,
        [listening],
    )

    // Once the dock has the station, it follows it; the card stops.
    useEffect(() => {
        if (!dockTunedIn || !listening) return
        sessionRef.current++
        setListening(false)
    }, [dockTunedIn, listening])

    // The card is the song's on-screen origin: while it is visible the dock
    // leaves the song playing here, and takes it once the card leaves.
    useEffect(() => {
        // Not while the dock plays the station: a visible origin would pull the
        // song back into the card.
        const originEl = originRef.current
        if (!playsHere || !mediaKey || !originEl || dockTunedIn) return undefined
        setMediaTrackOrigin(mediaKey, originEl)
        return () => clearMediaTrackOrigin(mediaKey, originEl)
    }, [playsHere, mediaKey, dockTunedIn]) // eslint-disable-line react-hooks/exhaustive-deps

    const endSession = useCallback(() => {
        sessionRef.current++
        setListening(false)
        clearInlineRadioPlay()
    }, [])

    // The coordinator says what happened to the card's song: an ended release
    // is the station's cue to move on; another song's claim means the
    // listener chose something else, and so does a pause from outside the
    // card; either ends the session here so the next advance never cuts in.
    const listeningRef = useRef(listening)
    listeningRef.current = listening
    const onPlayEndedRef = useRef(onPlayEnded)
    onPlayEndedRef.current = onPlayEnded
    const finishPlay = useCallback(
        (playIndex, finishedAt, retried = false) => {
            const duration = getMediaPlaybackState(mediaKey)?.duration
            return finishRadioNow({ playIndex, duration }).then(payload => {
                radioNowFeed.publish(payload)
                // The station refuses an early finish for an unmeasured song until
                // its minimum airtime; report once more after that, as the dock does.
                // Settles once the station has moved on (or gave up), so a handoff
                // never replays the song that just ended.
                if (retried || payload?.now?.playIndex !== playIndex) return undefined
                return new Promise(resolve => {
                    window.setTimeout(() => {
                        resolve(
                            listeningRef.current
                                ? finishPlay(playIndex, finishedAt, true)
                                : undefined,
                        )
                    }, radioFinishRetryDelayMs(finishedAt))
                })
            })
        },
        [mediaKey],
    ) // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (!listening || !mediaKey) return undefined
        // Native media fires pause just before ended, so a plain pause waits a
        // beat: an ended release (or the song sitting at its end) keeps the
        // session, anything else is the listener's own pause.
        let pauseTimer = null
        const unsubscribe = subscribeToMediaDock(event => {
            if (event.type === 'release' && event.ended && event.mediaKey === mediaKey) {
                window.clearTimeout(pauseTimer)
                const finished = finishPlay(clock?.playIndex, {
                    position: clock?.position,
                    receivedAtMs: receivedAt,
                })
                const session = sessionRef.current
                void finished.finally(() => {
                    if (sessionRef.current === session) onPlayEndedRef.current?.()
                })
            } else if (event.type === 'claim' && event.mediaKey && event.mediaKey !== mediaKey) {
                window.clearTimeout(pauseTimer)
                endSession()
            } else if (
                // A pause from elsewhere (the dock, system media keys) is the
                // listener's choice too; the dock taking the song over is not.
                event.type === 'release' &&
                !event.ended &&
                !event.takenOverByOwnerId &&
                event.mediaKey === mediaKey
            ) {
                window.clearTimeout(pauseTimer)
                pauseTimer = window.setTimeout(() => {
                    const state = getMediaPlaybackState(mediaKey)
                    const length = Number(state?.duration)
                    const atEnd =
                        coreRef.current?.getAudioElement()?.ended ||
                        (length > 0 && Number(state?.currentTime) >= length - 0.75)
                    if (!atEnd) endSession()
                }, 400)
            }
        })
        return () => {
            window.clearTimeout(pauseTimer)
            unsubscribe()
        }
    }, [listening, mediaKey, clock?.playIndex]) // eslint-disable-line react-hooks/exhaustive-deps

    // Tuned in here: play the song on air from the station's position, and the
    // next one as soon as the card swaps to it. A video song can't play in the
    // card, so the session moves to the dock's radio.
    const positionRef = useRef(position)
    positionRef.current = position
    const [startToken, setStartToken] = useState(0)
    // Keyed on the play index too: the same song drawn twice in a row is a new airing.
    useEffect(() => {
        if (!listening || !clock || dockTunedIn) return
        if (!playsHere) {
            setListening(false)
            clearInlineRadioPlay()
            openMediaDockRadio(`${source}_video`)
            return
        }
        const core = coreRef.current
        if (!core) return
        if (positionRef.current > 0) core.seekTo(positionRef.current)
        void core.play()
        rememberInlineRadioPlay(mediaKey, { ends_at: clock.endsAt })
    }, [listening, playsHere, mediaKey, clock?.playIndex, startToken, dockTunedIn]) // eslint-disable-line react-hooks/exhaustive-deps

    // "Play here" on a card the dock has the station for: the dock lets the
    // station go, and the card tunes in once it has.
    const [playHereRequested, setPlayHereRequested] = useState(false)
    const playHere = useCallback(
        event => {
            event?.stopPropagation()
            setPlayHereRequested(true)
            stopMediaDockRadio(`${source}_play_here`)
        },
        [source],
    )
    useEffect(() => {
        if (!playHereRequested || dockTunedIn || !playsHere) return
        setPlayHereRequested(false)
        sessionRef.current++
        setListening(true)
        setStartToken(token => token + 1)
    }, [playHereRequested, dockTunedIn, playsHere])

    const tuneIn = useCallback(() => {
        if (!playsHere) {
            openMediaDockRadio(source)
            return
        }
        sessionRef.current++
        setListening(true)
        setStartToken(token => token + 1)
    }, [playsHere, source])
    const tuneOut = useCallback(() => {
        sessionRef.current++
        setListening(false)
        coreRef.current?.pause()
        clearInlineRadioPlay()
        if (dockTunedIn) stopMediaDockRadio(source)
    }, [dockTunedIn, source])

    return {
        coreRef,
        mediaKey,
        playsHere,
        listening,
        tunedIn: (listening && inlinePlaying) || dockTunedIn,
        setInlinePlaying,
        endSession,
        tuneIn,
        tuneOut,
        playHere,
    }
}
